import { useState } from "react";
import type { ComponentProps } from "react";
import type { AppDataContextProvider } from "@src/components/AppDataContext";
import type OneTrustScript from "@src/components/partials/shared/OneTrustScript";

// Derived from the consumers' own prop types so the mapping is type-checked: a
// misnamed key here, or a renamed prop on either component, fails `tsc`. A JSX
// spread alone does not give us that — `<Provider {...{ wrongKey: true }} />`
// compiles and the real prop silently reads `undefined`.
type ConsentProps = Required<
  Pick<
    ComponentProps<typeof AppDataContextProvider>,
    "performanceCookieConsent" | "targetingCookieConsent" | "functionalCookieConsent"
  >
>;
type ConsentSetters = Required<
  Pick<
    ComponentProps<typeof OneTrustScript>,
    "setPerformanceCookieConsent" | "setTargetingCookieConsent" | "setFunctionalCookieConsent"
  >
>;

/**
 * OneTrust consent flags, kept together so the three similarly-named booleans
 * can be spread by name into `OneTrustScript` and `AppDataContextProvider`
 * rather than passed one by one (which is easy to cross-wire).
 *
 * The setters returned here are the raw `useState` setters — referentially
 * stable — because `OneTrustScript` uses them as effect dependencies for a
 * `OneTrustGroupsUpdated` listener it never removes. Wrapping them would
 * re-register that listener on every render.
 */
export const useCookieConsent = (): { consent: ConsentProps; setters: ConsentSetters } => {
  const [performanceCookieConsent, setPerformanceCookieConsent] = useState(false);
  const [targetingCookieConsent, setTargetingCookieConsent] = useState(false);
  const [functionalCookieConsent, setFunctionalCookieConsent] = useState(false);

  return {
    consent: { performanceCookieConsent, targetingCookieConsent, functionalCookieConsent },
    setters: { setPerformanceCookieConsent, setTargetingCookieConsent, setFunctionalCookieConsent },
  };
};
